<template>
  <div class="otp-input">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      :ref="`otp_${index}`"
      v-model="digits[index]"
      class="otp-input__box"
      type="text"
      inputmode="numeric"
      maxlength="1"
      autocomplete="one-time-code"
      :disabled="disabled"
      @keydown="onKeydown($event, index)"
      @input="onInput($event, index)"
      @paste.prevent="onPaste($event)"
      @focus="$event.target.select()"
    />
  </div>
</template>

<script>
import { parseOtpFromPaste, isValidOtpDigit } from '@/helpers/otpHelpers';

export default {
  name: 'OtpInput',

  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      digits: Array(6).fill(''),
    };
  },

  watch: {
    value(val) {
      const chars = String(val || '').slice(0, 6).split('');
      this.digits = Array.from({ length: 6 }, (_, i) => chars[i] || '');
    },
  },

  methods: {
    onInput(event, index) {
      const raw = event.target.value;

      // Accept only the last entered digit (handles autocomplete / mobile keyboards)
      const digit = raw.replace(/\D/g, '').slice(-1);
      this.$set(this.digits, index, digit);

      if (digit && index < 5) {
        this.$nextTick(() => this.focusBox(index + 1));
      }

      this.emitValue();
    },

    onKeydown(event, index) {
      if (event.key === 'Backspace') {
        if (this.digits[index]) {
          this.$set(this.digits, index, '');
          this.emitValue();
        } else if (index > 0) {
          this.$set(this.digits, index - 1, '');
          this.$nextTick(() => this.focusBox(index - 1));
          this.emitValue();
        }
        event.preventDefault();
      } else if (event.key === 'ArrowLeft' && index > 0) {
        this.focusBox(index - 1);
      } else if (event.key === 'ArrowRight' && index < 5) {
        this.focusBox(index + 1);
      } else if (!isValidOtpDigit(event.key) && !['Tab', 'Enter'].includes(event.key)) {
        // Block non-digit keys (except navigation/submit keys)
        if (event.key.length === 1) event.preventDefault();
      }
    },

    onPaste(event) {
      const text = (event.clipboardData || window.clipboardData).getData('text');
      const parsed = parseOtpFromPaste(text);

      parsed.forEach((digit, i) => {
        this.$set(this.digits, i, digit);
      });

      // Focus the box after the last pasted digit, or the last box
      this.$nextTick(() => {
        const nextIndex = Math.min(parsed.length, 5);
        this.focusBox(nextIndex);
      });

      this.emitValue();
    },

    focusBox(index) {
      const ref = this.$refs[`otp_${index}`];
      if (ref && ref[0]) ref[0].focus();
      else if (ref) ref.focus();
    },

    emitValue() {
      this.$emit('input', this.digits.join(''));
    },

    clear() {
      this.digits = Array(6).fill('');
      this.emitValue();
      this.$nextTick(() => this.focusBox(0));
    },
  },
};
</script>

<style scoped>
.otp-input {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.otp-input__box {
  width: 52px;
  height: 56px;
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  color: #1a1a2e;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
  caret-color: transparent;
}

.otp-input__box:focus {
  border-color: #3b5bdb;
  box-shadow: 0 0 0 3px rgba(59, 91, 219, 0.12);
}

.otp-input__box:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}
</style>
